import type {ComponentType} from "react";
import {SettingsModuleNav, SettingsPermissionsSubNav, SettingsSubNav} from "../../../Enums";
import {XyiconFeature} from "../../../generated/api/base";
import {ModuleSettingsV5} from "./modules/ModuleSettingsV5";
import {MyProfileV5} from "./myProfile/MyProfileV5";
import {OrganizationSettingsV5} from "./organization/OrganizationSettingsV5";
import {PermissionSetsV5} from "./permissions/permissionsets/PermissionSetsV5";
import {PortfolioGroupsViewV5} from "./permissions/portfoliogroups/PortfolioGroupsViewV5";
import {UserGroupsViewV5} from "./permissions/usergroups/UserGroupsViewV5";
import {UsersViewV5} from "./permissions/users/UsersViewV5";

export interface ISettingsSubMenu {
	id: SettingsSubNav | SettingsModuleNav | SettingsPermissionsSubNav;
	label: string;
	component?: ComponentType<any>;
	props?: any;
	children?: ISettingsSubMenu[];
}

export const SettingsComponentTree: ISettingsSubMenu[] = [
	{
		id: SettingsSubNav.PERSONAL,
		label: "My Profile",
		component: MyProfileV5,
	},
	{
		id: SettingsSubNav.ORGANIZATION,
		label: "Organization",
		component: OrganizationSettingsV5,
	},
	{
		id: SettingsSubNav.PERMISSIONS,
		label: "Permissions",
		children: [
			{
				id: SettingsPermissionsSubNav.USERS,
				label: "Users",
				component: UsersViewV5,
			},
			{
				id: SettingsPermissionsSubNav.USER_GROUPS,
				label: "User Groups",
				component: UserGroupsViewV5,
			},
			{
				id: SettingsPermissionsSubNav.PORTFOLIO_GROUPS,
				label: "Portfolio Groups",
				component: PortfolioGroupsViewV5,
			},
			{
				id: SettingsPermissionsSubNav.PERMISSION_SETS,
				label: "Permission Sets",
				component: PermissionSetsV5,
			},
		],
	},
	{
		id: SettingsSubNav.MODULES,
		label: "Modules",
		children: [
			{
				id: SettingsModuleNav.PORTFOLIO,
				label: "Portfolio",
				component: ModuleSettingsV5,
				props: {
					feature: XyiconFeature.Portfolio,
					icon: "portfolio",
				},
			},
			{
				id: SettingsModuleNav.SPACE,
				label: "Space",
				component: ModuleSettingsV5,
				props: {
					feature: XyiconFeature.Space,
					icon: "spaces",
				},
			},
			{
				id: SettingsModuleNav.CATALOG,
				label: "Catalog",
				component: ModuleSettingsV5,
				props: {
					feature: XyiconFeature.XyiconCatalog,
					icon: "catalog",
				},
			},
			{
				id: SettingsModuleNav.XYICON,
				label: "Xyicon",
				component: ModuleSettingsV5,
				props: {
					feature: XyiconFeature.Xyicon,
					icon: "xyicons",
				},
			},
			{
				id: SettingsModuleNav.BOUNDARY,
				label: "Boundary",
				component: ModuleSettingsV5,
				props: {
					feature: XyiconFeature.Boundary,
					icon: "boundaries",
				},
			},
		],
	},
];

export const getSettingsSubMenuById = (id: SettingsSubNav | SettingsPermissionsSubNav | SettingsModuleNav): ISettingsSubMenu => {
	const search = (items: ISettingsSubMenu[]): ISettingsSubMenu | undefined => {
		for (const item of items) {
			if (item.id === id) {
				return item;
			}
			if (item.children) {
				const subMenuMaybe = search(item.children);
				if (subMenuMaybe) {
					return subMenuMaybe;
				}
			}
		}
	};

	return search(SettingsComponentTree);
};

export const getSettingsComponentById = (id: SettingsSubNav | SettingsPermissionsSubNav | SettingsModuleNav): React.ReactElement<any, any> => {
	const subMenuMaybe = getSettingsSubMenuById(id);
	if (subMenuMaybe?.component) {
		const Component = subMenuMaybe.component;
		const props = subMenuMaybe.props || {};

		return <Component {...props} />;
	}

	console.warn(`SettingsViewV5: No component found for id ${id}`);

	return <></>;
};
